/**
 * dispatch_controller.js — the Dispatch board (Stimulus).
 *
 * Hosted on the page container in templates/calendar/dispatch.html, which
 * holds the board, the Unscheduled panel and the booking modal. The lanes, the
 * cards and their places on the grid are server-rendered; this controller owns
 * the gestures that change them:
 *
 *   BOOK FROM THE PANEL  drag a row onto a team member's time, or tap it and
 *                        then tap the time (touch has no native drag)
 *   BOOK A SLOT          click an empty time; the modal picks the quote or job
 *   MOVE A VISIT         drag a card to another team member, another time, or both
 *   OPEN A VISIT         click a card; the calendar's own event modal opens
 *                        (calendar/event-detail.js openEventDetail)
 *
 * Every write goes through the calendar event API the calendar page uses.
 * Creation is calendar/create-event.js postCalendarEvent, the single owner of
 * that payload and of the past-day rule; a move is one PUT with the new times
 * and, when the lane changed, the new crew.
 *
 * A move behaves like a FullCalendar drag: the card lands in its new place at
 * once, the PUT runs behind it, and a failed PUT puts the card back. The lanes
 * then catch up from the server by reloading only their Turbo frame, which
 * brings the card's time label, crew chips and data-event into line; that is
 * held back while another move is saving or a drag is under way, so a stale
 * render never snaps a card back. A booking or a change from the event modal
 * re-renders the whole page, because it also changes the Unscheduled list, which
 * is outside the frame: a modal change through the page check, a booking by
 * re-visiting the page (see _reload). Scroll position and an open panel survive.
 *
 * Crew semantics on a move are replace, deliberately: dropping a card on a
 * team member makes it THEIRS. Adding a second person is the picker's job, where
 * you can see who is already on it. A move within one lane keeps the crew.
 *
 * Without assign_work (canAssign false) no gesture puts anyone on a visit: a
 * visit moves only within its own lane, and work books only into Unassigned.
 * The server refuses the assignment either way; this keeps the board from
 * offering it.
 *
 * Pointer position becomes a time with the grid's own numbers (start hour, slot
 * length, slot count), measured along whichever axis time runs on the board
 * (data-time-axis, owned by dispatch_view_controller.js), read from the page's data-* values, which the server
 * takes from application/web/views/dispatch_board.py. Nothing about the grid's
 * shape is restated here; the one piece of its placement rule used here is the
 * instant preview of a move, which the lanes refresh then replaces.
 */
import { Controller } from "@hotwired/stimulus";
import {
    entityFromScheduleRow,
    isPastSlot,
    PAST_DAY_MESSAGE,
    postCalendarEvent,
    toLocalDatetimeStr
} from "../../calendar/create-event.js";
import { openEventDetail, visitFromData } from "../../calendar/event-detail.js";
import { clockLabel, createSlotHint } from "../../calendar/slot-hint.js";

// Same as the calendar's panel drop: a visit booked without a chosen length is
// an hour, and an all-day booking is stored from 9:00.
const DEFAULT_DURATION_MINUTES = 60;
const ALL_DAY_START_HOUR = 9;
const RESTORE_KEY = 'dispatch.restore';

const LANE = '[data-dispatch-target~="lane"]';
const CARD = '[data-dispatch-target~="card"]';
const PANEL_ROW = '.unscheduled-entity-item';
const BOOK_ROW = '.board-book-item';
// How long the picked slot shows before the booking sheet slides over the board,
// so a tap is seen to have landed on the right person and time.
const PICK_BEAT_MS = 180;

function notify(message, level) {
    if (typeof window.showNotification === 'function') window.showNotification(message, level);
}

function hhmm(minutes) {
    return `${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`;
}

export default class extends Controller {
    static targets = ["board", "lanes", "lane", "card", "bookModal", "bookHeading", "bookSearch",
                      "bookTime", "bookDuration", "bookSubmit"];
    static values = { url: String, day: String, startHour: Number, slotMinutes: Number, slotCount: Number,
                      canAssign: Boolean };

    connect() {
        this._drag = null;       // {card, grabOffset} or {row}
        this._armed = null;      // panel row armed for tap-to-place
        this._booking = null;    // {lane, row} while the booking modal is open
        this._saving = 0;        // moves whose PUT has not come back yet
        this._refreshWanted = false;
        this._hint = createSlotHint();
        // The slot a tap picked, held while its booking modal is open: on a phone there
        // is no hover marker, so this is the only sign the right time was hit.
        this._picked = createSlotHint('selected');
        this._panel = this.element.querySelector('#unscheduledPanel');

        this._bindBoard();
        this._bindPanel();
        this._restoreAfterReload();
    }

    disconnect() {
        this._listeners.forEach(([target, type, fn]) => target.removeEventListener(type, fn));
        this._listeners = [];
        this._disarm();
        this._hint.hide();
        this._picked.hide();
        document.body.classList.remove('unscheduled-open');
    }

    _listen(target, type, fn) {
        target.addEventListener(type, fn);
        this._listeners.push([target, type, fn]);
    }

    _bindBoard() {
        this._listeners = [];
        this._listen(this.element, 'dragstart', (e) => this._onDragStart(e));
        this._listen(this.element, 'dragend', () => this._endDrag());
        this._listen(this.element, 'dragover', (e) => this._onDragOver(e));
        this._listen(this.element, 'drop', (e) => this._onDrop(e));
        this._listen(this.element, 'click', (e) => this._onClick(e));
        this._listen(this.element, 'pointermove', (e) => this._onPointerMove(e));
        this._listen(this.element, 'pointerleave', () => this._clearHover());
        this._listen(document, 'keydown', (e) => { if (e.key === 'Escape') this._disarm(); });
        this._listen(this.element, 'turbo:before-frame-render', (e) => this._onLanesRender(e));
        this._listen(this.bookModalTarget, 'hidden.bs.modal', () => this._picked.hide());
        this._listen(this.bookTimeTarget, 'change', () => this._pickTime());
    }

    _bindPanel() {
        if (!this._panel) return;
        // Native drag, set here rather than in the shared row partial: the calendar
        // page drives the same rows through FullCalendar's own pointer dragging.
        this._panel.querySelectorAll(PANEL_ROW).forEach((row) => { row.draggable = true; });
        this._listen(this._panel, 'show.bs.offcanvas', () => document.body.classList.add('unscheduled-open'));
        this._listen(this._panel, 'hide.bs.offcanvas', () => {
            document.body.classList.remove('unscheduled-open');
            this._disarm();
        });
        const search = this._panel.querySelector('#unscheduledSearch');
        if (search) this._listen(search, 'input', () => this._filterRows(this._panel, PANEL_ROW, search.value));
    }

    /* ---------------------------------------------------------------- grid maths */

    _laneOf(el) {
        return el && el.closest ? el.closest(LANE) : null;
    }

    _isTimed(lane) {
        return lane.dataset.laneKind === 'timed';
    }

    /** Whether time runs across the board; dispatch_view_controller.js sets data-time-axis. */
    _timeAcross() {
        return this.boardTarget.dataset.timeAxis === 'across';
    }

    /** The pointer's viewport coordinate along the time axis. */
    _pointOf(event) {
        return this._timeAcross() ? event.clientX : event.clientY;
    }

    /** Where `el` starts along the time axis, and its length along it, in viewport px. */
    _span(el) {
        const rect = el.getBoundingClientRect();
        return this._timeAcross() ? [rect.left, rect.width] : [rect.top, rect.height];
    }

    /** Minutes after midnight of the slot at `point` (from _pointOf) in a timed lane. */
    _minutesAt(lane, point) {
        const [start, length] = this._span(lane);
        const slotSize = length / this.slotCountValue;
        const index = Math.min(this.slotCountValue - 1, Math.max(0, Math.floor((point - start) / slotSize)));
        return this.startHourValue * 60 + index * this.slotMinutesValue;
    }

    _dateAt(minutes) {
        const [year, month, day] = this.dayValue.split('-').map(Number);
        return new Date(year, month - 1, day, Math.floor(minutes / 60), minutes % 60);
    }

    /** Whether `lane` takes `card` (a move) or new work (no card) without assigning anyone the actor may not. */
    _mayDropOn(lane, card) {
        if (this.canAssignValue) return true;
        return card ? lane === this._laneOf(card) : Number.isNaN(parseInt(lane.dataset.userId, 10));
    }

    _crewFor(lane) {
        const userId = parseInt(lane.dataset.userId, 10);
        return Number.isNaN(userId) ? {} : { assignee_ids: [userId], lead_user_id: userId };
    }

    /* ---------------------------------------------------------------- hint */

    _showHint(lane, minutes, verb, hint = this._hint) {
        const slotSize = this._span(lane)[1] / this.slotCountValue;
        const index = (minutes - this.startHourValue * 60) / this.slotMinutesValue;
        hint.show(lane, index * slotSize, slotSize, `${verb} ${clockLabel(minutes)}`, this._timeAcross());
    }

    /** Mark the booking's slot, following the modal's time picker; none for all-day or no time. */
    _pickTime() {
        const time = this.bookTimeTarget.value;
        if (!this._booking || !this._isTimed(this._booking.lane) || !time) {
            this._picked.hide();
            return;
        }
        const minutes = Number(time.slice(0, 2)) * 60 + Number(time.slice(3, 5));
        this._showHint(this._booking.lane, minutes, 'Book', this._picked);
    }

    _clearHover() {
        this._hint.hide();
        this.laneTargets.forEach((lane) => lane.classList.remove('is-drop-target'));
    }

    _onPointerMove(event) {
        const lane = this._laneOf(event.target);
        if (event.pointerType !== 'mouse' || !lane || !this._isTimed(lane) || event.target.closest(CARD)) {
            this._hint.hide();
            return;
        }
        const minutes = this._minutesAt(lane, this._pointOf(event));
        if (isPastSlot(this._dateAt(minutes), false)) {
            this._hint.hide();
            return;
        }
        this._showHint(lane, minutes, this._armed ? 'Place at' : 'Book');
    }

    /* ---------------------------------------------------------------- dragging */

    _onDragStart(event) {
        const card = event.target.closest(CARD);
        const row = event.target.closest(PANEL_ROW);
        if (card) {
            // Where on the card it was grabbed, so the card's START lands on the slot under it.
            this._drag = { card, grabOffset: this._pointOf(event) - this._span(card)[0] };
            card.classList.add('is-dragging');
        } else if (row) {
            this._disarm();
            this._drag = { row, grabOffset: 0 };
        } else {
            return;
        }
        // Firefox will not start a drag without data on the transfer.
        event.dataTransfer.setData('text/plain', card ? card.dataset.eventId : row.dataset.scheduleId);
        event.dataTransfer.effectAllowed = card ? 'move' : 'copy';
    }

    _endDrag() {
        if (this._drag && this._drag.card) this._drag.card.classList.remove('is-dragging');
        this._drag = null;
        this._clearHover();
        // After the drop handler has run, so a refresh held back by this drag
        // does not start ahead of the move the drop is about to make.
        if (this._refreshWanted) setTimeout(() => this._refreshLanes(), 0);
    }

    _onDragOver(event) {
        const lane = this._laneOf(event.target);
        if (!lane || !this._drag || !this._mayDropOn(lane, this._drag.card)) return;
        event.preventDefault();          // preventDefault IS the "droppable" signal
        this._clearHover();
        lane.classList.add('is-drop-target');
        if (this._isTimed(lane)) {
            this._showHint(lane, this._minutesAt(lane, this._pointOf(event) - this._drag.grabOffset),
                           this._drag.card ? 'Move to' : 'Book');
        }
    }

    _onDrop(event) {
        const lane = this._laneOf(event.target);
        if (!lane || !this._drag || !this._mayDropOn(lane, this._drag.card)) return;
        event.preventDefault();
        const drag = this._drag;
        const minutes = this._isTimed(lane) ? this._minutesAt(lane, this._pointOf(event) - drag.grabOffset) : null;
        this._endDrag();
        if (drag.card) this._moveCard(drag.card, lane, minutes);
        else this._bookRow(drag.row, lane, minutes, DEFAULT_DURATION_MINUTES);
    }

    /* ---------------------------------------------------------------- clicks */

    _onClick(event) {
        const row = event.target.closest(PANEL_ROW);
        if (row) {
            event.preventDefault();
            if (row === this._armed) this._disarm(); else this._arm(row);
            return;
        }
        const bookRow = event.target.closest(BOOK_ROW);
        if (bookRow) {
            event.preventDefault();
            this._selectBookRow(bookRow);
            return;
        }
        const card = event.target.closest(CARD);
        if (card) {
            this._openVisit(card);
            return;
        }
        const lane = this._laneOf(event.target);
        if (!lane || !this._mayDropOn(lane)) return;
        const minutes = this._isTimed(lane) ? this._minutesAt(lane, this._pointOf(event)) : null;
        if (this._armed) {
            const row = this._armed;
            this._disarm();
            this._bookRow(row, lane, minutes, DEFAULT_DURATION_MINUTES);
        } else {
            this._openBooking(lane, minutes);
        }
    }

    // A change from the modal re-renders through the page check: re-visiting this
    // URL paints the board saved before the change, so a deleted visit's card
    // comes back (PWA_CACHE_STRATEGY.md, write contract rule 3).
    _openVisit(card) {
        openEventDetail(visitFromData(card.dataset.event), () => {
            this._rememberView();
            window.chippiePageCheck.refresh();
        });
    }

    _arm(row) {
        this._disarm();
        this._armed = row;
        row.classList.add('is-armed');
        document.body.classList.add('unscheduled-armed');
    }

    _disarm() {
        if (this._armed) this._armed.classList.remove('is-armed');
        this._armed = null;
        document.body.classList.remove('unscheduled-armed');
    }

    /* ---------------------------------------------------------------- writes */

    /**
     * Book a schedulable row into a lane. `minutes` null means the all-day strip.
     * @returns {Promise<boolean>} true once booked (the page is reloading)
     */
    async _bookRow(row, lane, minutes, durationMinutes) {
        const allDay = minutes === null;
        const start = this._dateAt(allDay ? ALL_DAY_START_HOUR * 60 : minutes);
        if (isPastSlot(start, allDay)) {
            notify(PAST_DAY_MESSAGE, 'error');
            return false;
        }
        const end = new Date(start.getTime() + durationMinutes * 60000);
        try {
            await postCalendarEvent({ ...entityFromScheduleRow(row), start, end, allDay, crew: this._crewFor(lane) });
        } catch (error) {
            notify(error.message || 'Could not book that visit.', 'error');
            return false;
        }
        this._reload();
        return true;
    }

    /** The PUT body for moving `card` into `lane` at `minutes`, or null when nothing changes. */
    _moveBody(card, lane, minutes) {
        const body = {};
        const wasAllDay = card.dataset.allDay === 'true';
        if (minutes !== null && (wasAllDay || card.dataset.start !== hhmm(minutes))) {
            const start = this._dateAt(minutes);
            const duration = (!wasAllDay && parseInt(card.dataset.durationMinutes, 10)) || DEFAULT_DURATION_MINUTES;
            Object.assign(body, {
                start_datetime: toLocalDatetimeStr(start),
                end_datetime: toLocalDatetimeStr(new Date(start.getTime() + duration * 60000)),
                all_day: false
            });
        } else if (minutes === null && !wasAllDay) {
            body.all_day = true;
        }
        if (lane.dataset.userId !== this._laneOf(card).dataset.userId) {
            body.assignee_ids = this._crewFor(lane).assignee_ids || [];
        }
        return Object.keys(body).length ? body : null;
    }

    async _moveCard(card, lane, minutes) {
        const body = this._moveBody(card, lane, minutes);
        if (!body) return;   // dropped back where it came from

        const undo = this._placeCard(card, lane, minutes, body);
        // urlValue is built with event_id=0 as a placeholder, because the
        // template cannot know the id of the card that will be dragged.
        const url = this.urlValue.replace(/0$/, card.dataset.eventId);
        this._saving++;
        try {
            const response = await fetch(url, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(body)
            });
            if (!response.ok) throw new Error(String(response.status));
        } catch (err) {
            console.error('Move failed:', err);
            undo();
            notify('Could not move that visit.', 'error');
        } finally {
            this._saving--;
        }
        this._refreshLanes();
    }

    /**
     * Show a move before the server confirms it. The card's place is a preview of
     * dispatch_board.place_visit (the owner), replaced when the lanes refresh.
     * The card's data-* are updated too, so a second drag of the same card before
     * that refresh compares against where it now is.
     * @returns {Function} undo, restoring the card exactly as it was
     */
    _placeCard(card, lane, minutes, body) {
        const before = {
            parent: card.parentElement, next: card.nextElementSibling,
            style: card.getAttribute('style'), data: { ...card.dataset }
        };
        const fromColumn = card.closest('section');

        lane.appendChild(card);
        if (minutes === null) {
            card.removeAttribute('style');
            card.dataset.allDay = 'true';
        } else if (body.start_datetime) {
            const duration = (new Date(body.end_datetime) - new Date(body.start_datetime)) / 60000;
            const index = (minutes - this.startHourValue * 60) / this.slotMinutesValue;
            const span = Math.max(1, Math.min(Math.ceil(duration / this.slotMinutesValue), this.slotCountValue - index));
            card.style.setProperty('--slot', index + 1);
            card.style.setProperty('--span', span);
            Object.assign(card.dataset, { start: hhmm(minutes), durationMinutes: duration, allDay: 'false' });
        }
        if (body.assignee_ids) card.dataset.assigneeIds = body.assignee_ids.join(',');
        this._recount(fromColumn, lane.closest('section'));

        return () => {
            before.parent.insertBefore(card, before.next && before.next.parentElement === before.parent ? before.next : null);
            if (before.style === null) card.removeAttribute('style'); else card.setAttribute('style', before.style);
            Object.keys(card.dataset).forEach((key) => { delete card.dataset[key]; });
            Object.assign(card.dataset, before.data);
            this._recount(fromColumn, lane.closest('section'));
        };
    }

    /** Each column's header badge counts the cards in its lanes. */
    _recount(...columns) {
        columns.forEach((column) => {
            const badge = column && column.querySelector('.dispatch-head .badge');
            if (badge) badge.textContent = column.querySelectorAll(CARD).length;
        });
    }

    /* ---------------------------------------------------------------- lanes refresh */

    /** Bring the lanes into line with the server, unless a move could be undone by it. */
    _refreshLanes() {
        if (this._saving > 0 || this._drag) {
            this._refreshWanted = true;
            return;
        }
        this._refreshWanted = false;
        const frame = this.lanesTarget;
        if (frame.src) frame.reload(); else frame.src = window.location.href;
    }

    // A render that started before the latest move saved would show the old
    // place; skip it and ask again once nothing is in flight.
    _onLanesRender(event) {
        if (event.target !== this.lanesTarget) return;
        if (this._saving > 0 || this._drag) {
            event.detail.render = () => {};
            this._refreshWanted = true;
        }
    }

    /* ---------------------------------------------------------------- booking modal */

    _openBooking(lane, minutes) {
        if (isPastSlot(this._dateAt(minutes === null ? 0 : minutes), minutes === null)) {
            notify(PAST_DAY_MESSAGE, 'error');
            return;
        }
        this._booking = { lane, row: null };
        const dayLabel = this._dateAt(0).toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short' });
        this.bookHeadingTarget.textContent = `${lane.dataset.userName} · ${dayLabel}`;
        this.bookTimeTarget.value = minutes === null ? '' : hhmm(minutes);
        this._hint.hide();
        this._pickTime();
        this.bookSearchTarget.value = '';
        this._filterRows(this.bookModalTarget, BOOK_ROW, '');
        this.bookModalTarget.querySelectorAll(BOOK_ROW).forEach((el) => el.classList.remove('active'));
        this.bookSubmitTarget.disabled = true;
        const modal = window.bootstrap.Modal.getOrCreateInstance(this.bookModalTarget);
        if (this._picked.isShown()) setTimeout(() => modal.show(), PICK_BEAT_MS);
        else modal.show();
    }

    _selectBookRow(row) {
        if (!this._booking) return;
        this.bookModalTarget.querySelectorAll(BOOK_ROW).forEach((el) => el.classList.toggle('active', el === row));
        this._booking.row = row;
        this.bookSubmitTarget.disabled = false;
    }

    filterBookList() {
        this._filterRows(this.bookModalTarget, BOOK_ROW, this.bookSearchTarget.value);
    }

    async book() {
        if (!this._booking || !this._booking.row) return;
        const time = this.bookTimeTarget.value;
        const minutes = time ? Number(time.slice(0, 2)) * 60 + Number(time.slice(3, 5)) : null;
        this.bookSubmitTarget.disabled = true;
        const booked = await this._bookRow(this._booking.row, this._booking.lane, minutes,
                                           parseInt(this.bookDurationTarget.value, 10) || DEFAULT_DURATION_MINUTES);
        if (!booked) this.bookSubmitTarget.disabled = false;
    }

    _filterRows(scope, selector, query) {
        const needle = (query || '').toLowerCase();
        scope.querySelectorAll(selector).forEach((row) => {
            row.style.display = row.textContent.toLowerCase().includes(needle) ? '' : 'none';
        });
    }

    /* ---------------------------------------------------------------- reload */

    /**
     * Re-render from the server after a booking, keeping the board's scroll and
     * an open panel. Still a visit of this URL, not the page check: the check
     * never swaps while the Unscheduled panel is open, and a booking usually
     * starts from it.
     */
    _reload() {
        this._rememberView();
        if (typeof Turbo !== 'undefined' && Turbo.visit) {
            Turbo.visit(window.location.href, { action: 'replace' });
        } else {
            window.location.reload();
        }
    }

    /** Save the board's scroll and panel state for the page that replaces this one. */
    _rememberView() {
        const state = {
            top: this.hasBoardTarget ? this.boardTarget.scrollTop : 0,
            left: this.hasBoardTarget ? this.boardTarget.scrollLeft : 0,
            panelOpen: !!(this._panel && this._panel.classList.contains('show'))
        };
        try { sessionStorage.setItem(RESTORE_KEY, JSON.stringify(state)); } catch { /* private mode */ }
    }

    _restoreAfterReload() {
        let state = null;
        try {
            state = JSON.parse(sessionStorage.getItem(RESTORE_KEY) || 'null');
            sessionStorage.removeItem(RESTORE_KEY);
        } catch {
            return;
        }
        if (!state) return;
        if (this.hasBoardTarget) {
            this.boardTarget.scrollTop = state.top;
            this.boardTarget.scrollLeft = state.left;
        }
        if (state.panelOpen && this._panel && window.bootstrap) {
            window.bootstrap.Offcanvas.getOrCreateInstance(this._panel).show();
        }
    }
}
